import React, { useState, useEffect } from 'react';
import { User, PersonalDataUpdateRequest } from '../../types/auth';

interface PersonalDataFormProps {
    user: User;
    onSubmit: (data: PersonalDataUpdateRequest) => void;
}

export const PersonalDataForm: React.FC<PersonalDataFormProps> = ({ user, onSubmit }) => {
    const [formData, setFormData] = useState<PersonalDataUpdateRequest>({
        firstName: '',
        lastName: '',
        middleName: '',
        phoneNumber: '',
        email: '',
        iin: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (user) {
            // Если email пустой, но login содержит @, используем login как email
            const emailValue = user.email && user.email.trim() !== ''
                ? user.email
                : (user.login && user.login.includes('@') ? user.login : '');

            // Если phoneNumber пустой, но login НЕ содержит @, используем login как телефон
            const phoneValue = user.phoneNumber && user.phoneNumber.trim() !== ''
                ? user.phoneNumber
                : (user.login && !user.login.includes('@') ? user.login : '');

            setFormData({
                firstName: user.fullName?.split(' ')[1] || '',
                lastName: user.fullName?.split(' ')[0] || '',
                middleName: user.fullName?.split(' ')[2] || '',
                phoneNumber: phoneValue,
                email: emailValue,
                iin: user.iin || ''
            });
        }
    }, [user]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            await onSubmit(formData);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-2xl">
            <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Личные данные</h2>
                <p className="text-gray-600">Обновите ваши личные данные и контактную информацию</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Фамилия */}
                    <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                            Фамилия *
                        </label>
                        <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Введите фамилию"
                        />
                    </div>

                    {/* Имя */}
                    <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                            Имя *
                        </label>
                        <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Введите имя"
                        />
                    </div>

                    {/* Отчество */}
                    <div>
                        <label htmlFor="middleName" className="block text-sm font-medium text-gray-700 mb-2">
                            Отчество
                        </label>
                        <input
                            type="text"
                            id="middleName"
                            name="middleName"
                            value={formData.middleName}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Введите отчество"
                        />
                    </div>

                    {/* ИИН - неактивное поле */}
                    <div>
                        <label htmlFor="iin" className="block text-sm font-medium text-gray-700 mb-2">
                            ИИН
                        </label>
                        <input
                            type="text"
                            id="iin"
                            name="iin"
                            value={formData.iin}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                            placeholder="12 цифр ИИН"
                        />
                    </div>

                    {/* Телефон - неактивное поле */}
                    <div>
                        <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                            Номер телефона
                        </label>
                        <input
                            type="tel"
                            id="phoneNumber"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                            placeholder="+7XXXXXXXXXX"
                        />
                    </div>

                    {/* Email - неактивное поле */}
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                            Email
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                            placeholder="example@email.com"
                        />
                    </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end pt-6 border-t border-gray-200">
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {isSubmitting ? 'Сохранение...' : 'Сохранить изменения'}
                    </button>
                </div>
            </form>
        </div>
    );
};
